import { CircularProgress, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { addToast } from '@sophocles/features/toast';
import { DISPATCH_STATUS } from '@sophocles/hooks/commandDispatchResult';
import useCommandDispatch from '@sophocles/hooks/useCommandDispatch';

const COMMAND_BY_CHOICE = {
  arm_intrusion_sensor: 'ARM_INTRUSION_SENSOR',
  disarm_intrusion_sensor: 'DISARM_INTRUSION_SENSOR',
};

function IntrusionCommandsDialog({ site, open, closeDialog }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [choice, setChoice] = useState(null);

  const siteId = site.get('id', 0);
  const { dispatchCommands, submitting, activityLocked } = useCommandDispatch(siteId);

  const selectChoice = val => () => setChoice(prev => (prev === val ? null : val));

  const getDescription = () => {
    switch (choice) {
      case 'arm_intrusion_sensor':
        return t('sites.intrusion.armDescription', 'This will arm your intrusion sensor.');
      case 'disarm_intrusion_sensor':
        return t(
          'sites.intrusion.disarmDescription',
          "This will disarm your intrusion sensor.  It will be disarmed for as long as your 'Rearm Delay' setting.",
        );
      default:
        return t('sites.intrusion.selectCommandInfo', 'Select a command to get more information about it.');
    }
  };

  const submitCommand = () => {
    if (submitting || activityLocked) return;

    const commandName = COMMAND_BY_CHOICE[choice] ?? null;

    if (siteId !== 0 && commandName != null) {
      dispatchCommands([{ key: commandName, params: {} }]).then(result => {
        if (result.status === DISPATCH_STATUS.SUCCESS) {
          closeDialog();
        } else {
          dispatch(
            addToast({
              message: result.messages[0] ?? t('commandCatalog.sendError', 'Failed to send command.'),
              level: 'error',
            }),
          );
        }
      });
    }
    setChoice(null);
  };

  const actions = [
    <Button key="cancel" variant="outlined" onClick={closeDialog}>
      {t('actions.cancel', 'Cancel')}
    </Button>,
    <Button
      key="submit"
      variant="contained"
      disabled={choice == null || submitting || activityLocked}
      startIcon={submitting ? <CircularProgress color="inherit" size={16} /> : null}
      onClick={submitCommand}
    >
      {submitting ? t('commandCatalog.sending') : t('general.submit', 'Submit')}
    </Button>,
  ];

  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogTitle>{t('sites.intrusion.unitCommands', 'Intrusion Unit Commands')}</DialogTitle>
      <DialogContent dividers>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ fontSize: '16px' }}>
            <div>
              <div className="form-check">
                <label className="form-check-label">
                  <input
                    className="form-check-input"
                    type="radio"
                    value="arm_intrusion_sensor"
                    checked={choice == 'arm_intrusion_sensor'}
                    disabled={activityLocked || submitting}
                    onChange={selectChoice('arm_intrusion_sensor')}
                    name="command"
                  />{' '}
                  {t('sites.intrusion.armSensor', 'Arm Intrusion Sensor')}
                </label>
              </div>
              <div className="form-check">
                <label className="form-check-label">
                  <input
                    className="form-check-input"
                    type="radio"
                    value="disarm_intrusion_sensor"
                    checked={choice == 'disarm_intrusion_sensor'}
                    disabled={activityLocked || submitting}
                    onChange={selectChoice('disarm_intrusion_sensor')}
                    name="command"
                  />{' '}
                  {t('sites.intrusion.disarmSensor', 'Disarm Intrusion Sensor')}
                </label>
              </div>
            </div>
          </div>
          <span>
            <i className="fas fa-info-circle" /> {getDescription()}{' '}
          </span>
        </div>
      </DialogContent>
      <DialogActions>{actions}</DialogActions>
    </Dialog>
  );
}

export default IntrusionCommandsDialog;
